import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { FileText } from 'lucide-react';
import { getUserCompanyDepartments } from '../../../api/PreformanceManagement/Goals';
import FilterPanel from './components/FilterPanel';
import PayslipsSection from './components/PayslipsSection';
import BulkGenerationPanel from './components/BulkGenerationPanel';
import PayslipPreviewModal from '@/components/payslip/PayslipPreviewModal';
import type { PayslipData } from '@/components/payslip/PayslipPreviewModal';
import {
  GetUserPayslips,
  BulkNotifyPayslips,
  PreviewPayslipPdf,
  DownloadPayslipPdf,
  type PagedResult,
} from '@/api/Payroll/userPayslip';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { apiErrorsOf } from '@/utils/requestHandling';
import Button from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../components/ui/Dialog';
import { Alert, AlertDescription } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { PageLayout } from '../../../components/ui/PageLayout';
import {
  type InternalEmployee,
  type DisplayEmployee,
  type DepartmentFromAPI,
  convertToDisplayEmployee,
  getMonthNumber,
  mapApiItemToInternalEmployee,
  extractPayrollCycles,
  filterEmployeesByCycle,
} from './helpers/payslipDataTransformers';
import {
  executeBulkConfirmation,
  executePayslipGeneration,
  executeBulkGenerationByDepartment,
  executeCreatePayslip,
  type BulkOperationDeps,
} from './helpers/payslipBulkOperations';

interface Filters {
  department: string;
  departmentId: number | null;
  payrollCycle: string;
  year: string;
  month: string;
  search: string;
}

interface Props {
  company?: {
    id: number;
    name: string;
    location?: string;
    logoUrl?: string;
  };
  currencySymbol?: string;
}

const PayslipsPage: React.FC<Props> = ({ company, currencySymbol = 'AED' }) => {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [internalEmployees, setInternalEmployees] = useState<InternalEmployee[]>([]);
  const [displayEmployees, setDisplayEmployees] = useState<DisplayEmployee[]>([]);
  const [payrollCycles, setPayrollCycles] = useState<string[]>([]);
  const [departmentsFromAPI, setDepartmentsFromAPI] = useState<DepartmentFromAPI[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedPayslips, setSelectedPayslips] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkOperation, setBulkOperation] = useState<'download' | 'notify' | 'generate' | null>(null);
  const [bulkGenerationLoading, setBulkGenerationLoading] = useState(false);
  const [availableMonths, setAvailableMonths] = useState<{ value: string; label: string }[]>([]);
  const [filters, setFilters] = useState<Filters>({
    department: '',
    departmentId: null,
    payrollCycle: '',
    year: new Date().getFullYear().toString(),
    month: '', // Empty to load all months
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEmployee, setPreviewEmployee] = useState<DisplayEmployee | null>(null);

  const findDepartmentId = useCallback((dept: any): number | undefined => {
    if (!dept) return undefined;
    if (typeof dept === 'object' && dept.id) return dept.id;
    if (typeof dept === 'string') return departmentsFromAPI.find(d => d.name.toLowerCase() === dept.toLowerCase())?.id;
    return undefined;
  }, [departmentsFromAPI]);

  const pageSize = 25;

  const loadEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const input: any = {
        pageSize,
        pageNumber: currentPage
      };
      if (filters.departmentId) input.departmentId = filters.departmentId;
      if (filters.year) input.year = parseInt(filters.year);
      if (filters.month) {
        const monthNum = parseInt(filters.month.split('-')[0]);
        if (!isNaN(monthNum)) input.month = monthNum;
      }

      const result = await GetUserPayslips(input);
      if (result.status === 200 && result.payload?.items) {
        const mapped = result.payload.items.map(mapApiItemToInternalEmployee);
        setInternalEmployees(mapped);
        setDisplayEmployees(mapped.map(convertToDisplayEmployee));
        setTotalCount(result.payload.totalCount ?? 0);
        const cycles = extractPayrollCycles(mapped);
        setPayrollCycles(cycles);

        // Build available months dropdown options
        const monthOpts = cycles.map(c => {
          const date = new Date(`${c.split(' ')[0]} 1, ${c.split(' ')[1]}`);
          const month = date.getMonth() + 1;
          const year = date.getFullYear();
          return { value: `${month}-${year}`, label: c };
        });
        setAvailableMonths(monthOpts);

        setSelectedCycle(prev => prev || (cycles.length > 0 ? cycles[0] : prev));
      } else {
        setDisplayEmployees([]); setInternalEmployees([]); setPayrollCycles([]); setAvailableMonths([]);
        setTotalCount(0);
      }
    } catch {
      showToast(t('Failed to load employees. Please try again.'), 'error');
      setDisplayEmployees([]); setInternalEmployees([]); setPayrollCycles([]); setAvailableMonths([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters.departmentId, filters.year, filters.month, currentPage, showToast, t]);

  useEffect(() => {
    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const departments = await getUserCompanyDepartments();
        setDepartmentsFromAPI(departments.map(dept => ({ id: dept.id || 0, name: dept.name || '' })));
      } catch { showToast(t('Failed to load departments'), 'error'); }
      finally { setLoadingDepartments(false); }
    };
    fetchDepartments();
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const bulkDeps = useMemo((): BulkOperationDeps => ({
    showToast, t, internalEmployees, filters, findDepartmentId, loadEmployees,
  }), [showToast, t, internalEmployees, filters, findDepartmentId, loadEmployees]);

  const handleCycleChange = (cycle: string) => {
    setSelectedCycle(cycle);
    const [monthName, year] = cycle.split(' ');
    setFilters(prev => ({ ...prev, month: getMonthNumber(monthName).toString().padStart(2, '0'), year }));
    setCurrentPage(1);
  };

  const handlePayslipPreview = async (employee: DisplayEmployee) => {
    // Always show the preview modal regardless of payslip status
    setPreviewEmployee(employee);
    setPreviewOpen(true);
  };

  const handlePayslipDownload = async (employee: DisplayEmployee): Promise<void> => {
    if (employee.payslipStatus === 'Generated' && employee.id) {
      try { await DownloadPayslipPdf(employee.id); showToast(t('Payslip downloaded successfully!'), 'success'); }
      catch { showToast(t('Failed to download payslip. Please try again.'), 'error'); }
    } else { showToast(t('Payslip not generated yet. Please generate it first.'), 'info'); }
  };

  const handlePayslipGenerate = async (employee: DisplayEmployee): Promise<void> => {
    setIsLoading(true);
    try { await executePayslipGeneration(employee, bulkDeps); }
    catch { showToast(t('Failed to generate payslip. Please try again.'), 'error'); }
    finally { setIsLoading(false); }
  };

  const handlePayslipRegenerate = async (employee: DisplayEmployee): Promise<void> => {
    setIsLoading(true);
    try { await executePayslipGeneration(employee, bulkDeps); }
    catch { showToast(t('Failed to regenerate payslip. Please try again.'), 'error'); }
    finally { setIsLoading(false); }
  };

  const handlePayslipNotify = async (employee: DisplayEmployee): Promise<void> => {
    if (employee.payslipStatus !== 'Generated') {
      showToast(t('Payslip must be generated before sending notification.'), 'info'); return;
    }
    setIsLoading(true);
    try {
      const result = await BulkNotifyPayslips({ payslipIds: [employee.id] });
      if (result.status === 200) { showToast(t('Notification sent to') + ` ${employee.name}`, 'success'); setTimeout(() => loadEmployees(), 1000); }
      else { showToast(result.errors?.[0]?.description || t('Failed to send notification'), 'error'); }
    } catch { showToast(t('Failed to send notification. Please try again.'), 'error'); }
    finally { setIsLoading(false); }
  };

  const convertMonthNameToKey = (monthName: string, year: string): string => {
    const monthIndex = ['January','February','March','April','May','June',
      'July','August','September','October','November','December'].indexOf(monthName);
    return monthIndex >= 0 ? `${monthIndex + 1}-${year}` : 'all';
  };

  const handleFilterChange = (filterName: keyof Filters, value: string) => {
    setFilters(prev => {
      const next = { ...prev, [filterName]: value };
      if (filterName === 'department') next.departmentId = departmentsFromAPI.find(d => d.name === value)?.id ?? null;
      return next;
    });
    if (filterName === 'month') {
      if (value) {
        // value is "M-YYYY" format - find matching cycle label
        const match = availableMonths.find(m => m.value === value);
        if (match) setSelectedCycle(match.label);
      } else {
        setSelectedCycle('');
      }
    }
    setSelectedPayslips([]);
    setCurrentPage(1);
  };

  const handleBulkOperation = (op: 'download' | 'notify' | 'generate') => {
    if (!selectedPayslips.length) { showToast(t('Please select at least one payslip'), 'info'); return; }
    setBulkOperation(op); setBulkDialogOpen(true);
  };

  const confirmBulkOperation = async () => {
    if (!bulkOperation || !selectedPayslips.length) return;
    setIsBulkProcessing(true);
    try { await executeBulkConfirmation(bulkOperation, selectedPayslips, bulkDeps); }
    // CO-575 w1: `error.response` is never set on a requestHandling rejection (the axios response is
    // unwrapped), so the old chain was a dead read and this always showed the generic fallback.
    catch (error: any) { showToast(getApiErrorMessage(error, t, t('Failed to perform bulk operation')), 'error'); }
    finally { setIsBulkProcessing(false); setBulkDialogOpen(false); setBulkOperation(null); setSelectedPayslips([]); }
  };

  const handleCreatePayslip = async (employee: DisplayEmployee) => {
    setIsLoading(true);
    try { await executeCreatePayslip(employee, bulkDeps); }
    // CO-575 w1: dead `error.response?.data` read replaced by the shared accessor.
    catch (error: any) { showToast(getApiErrorMessage(error, t, t('Failed to create payslip. Please try again.')), 'error'); }
    finally { setIsLoading(false); }
  };

  const handleBulkGenerationByDepartment = async (input: any) => {
    setBulkGenerationLoading(true);
    try { await executeBulkGenerationByDepartment(input, departmentsFromAPI, { showToast, t, loadEmployees }); }
    catch (error: any) {
      // CO-575 w1: every `error.response?.status` branch below was UNREACHABLE — the interceptor
      // rejects with the parsed body, which has no `response`. Read the status off the body
      // (`status` / hidden `__httpStatus`) so the bespoke copy can fire, and prefer the SERVER's
      // own refusal text whenever the body carried one.
      const status: number | undefined = error?.status ?? error?.__httpStatus ?? error?.response?.status;
      let fallback = t('Bulk generation failed. Please try again.');
      if (status === 404) fallback = t('Payslip generation service is not available.');
      else if (status === 403) fallback = t("You don't have permission to perform bulk payslip generation.");
      else if (status === 400) fallback = t('Invalid request.');
      else if (typeof status === 'number' && status >= 500) fallback = t('Server error occurred. Please try again later.');
      else if (!status && error?.request) fallback = t('Network error. Please check your connection.');
      const msg = apiErrorsOf(error).length > 0 ? getApiErrorMessage(error, t, fallback) : fallback;
      showToast(msg, 'error');
    } finally { setBulkGenerationLoading(false); }
  };

  const filteredEmployees = filterEmployeesByCycle(displayEmployees, selectedCycle);
  const allSelectedGenerated = selectedPayslips.length > 0 && selectedPayslips.every(id => displayEmployees.find(e => e.id === id)?.payslipStatus === 'Generated');
  const hasGeneratedSelected = selectedPayslips.some(id => displayEmployees.find(e => e.id === id)?.payslipStatus === 'Generated');
  const allNotified = selectedPayslips.length > 0 && selectedPayslips.every(id => displayEmployees.find(e => e.id === id)?.payslipStatus === 'Notified');

  const notifyTooltip = !selectedPayslips.length ? t('Please select payslips to notify')
    : allNotified ? t('All selected employees have already been notified')
    : !hasGeneratedSelected ? t('Employees have to have their payslips generated to be notified') : '';

  const departmentOptions = departmentsFromAPI.map(d => d.name);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedPayslips([]);
  };

  return (
    <PageLayout
      title={t('Payslips')}
      description={t('Manage and generate employee payslips')}
      icon={FileText}
      accentColor="emerald"
      maxWidth="1600px"
      headerActions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadEmployees} disabled={isLoading} iconName="RefreshCw" loading={isLoading}>
            {t('Refresh')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleBulkOperation('download')} disabled={!selectedPayslips.length || isBulkProcessing} iconName="Download">
            {t('Download Selected')}
          </Button>
          <span title={notifyTooltip}>
            <Button variant="outline" size="sm" onClick={() => handleBulkOperation('notify')} disabled={!selectedPayslips.length || isBulkProcessing || !hasGeneratedSelected} iconName="Bell">
              {t('Notify Employee')}
            </Button>
          </span>
        </div>
      }
    >
    <div className="relative">
      <ToastRenderer toast={toast} onDismiss={dismissToast} />

      <BulkGenerationPanel onBulkGenerate={handleBulkGenerationByDepartment} isLoading={bulkGenerationLoading}
        departments={departmentOptions} departmentOptions={departmentsFromAPI} loadingDepartments={loadingDepartments} />

      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        departments={departmentOptions}
        loadingDepartments={loadingDepartments}
        availableMonths={availableMonths}
      />

      <div className="mt-4" data-testid="payslips-table">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[200px] gap-2">
            <Spinner size="md" data-testid="loading-spinner" />
            <span className="text-sm text-muted-foreground">{t('Loading payslips...')}</span>
          </div>
        ) : (
          <PayslipsSection
            employees={filteredEmployees}
            payrollCycles={payrollCycles}
            selectedCycle={selectedCycle}
            selectedPayslips={selectedPayslips}
            isLoading={isLoading}
            onCycleChange={handleCycleChange}
            onPayslipSelection={setSelectedPayslips}
            onPayslipPreview={handlePayslipPreview}
            onPayslipDownload={handlePayslipDownload}
            onPayslipGenerate={handlePayslipGenerate}
            onPayslipRegenerate={handlePayslipRegenerate}
            onPayslipNotify={handlePayslipNotify}
            onCreatePayslip={handleCreatePayslip}
            searchTerm={filters.search}
            currencySymbol={currencySymbol}
            currentPage={currentPage}
            totalCount={totalCount}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <PayslipPreviewModal
        isOpen={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewEmployee(null); }}
        isHrView
        payslip={previewEmployee ? {
          id: previewEmployee.id,
          name: previewEmployee.name,
          employeeId: previewEmployee.employeeId,
          department: previewEmployee.department,
          basicSalary: previewEmployee.basicSalary,
          variablePay: previewEmployee.variablePay,
          deductions: previewEmployee.deductions,
          netPay: previewEmployee.netPay,
          adjustments: previewEmployee.adjustments,
          adjustmentAdditions: previewEmployee.adjustmentAdditions,
          adjustmentDeductions: previewEmployee.adjustmentDeductions,
          overtimePay: previewEmployee.overtimePay,
          overtimeActualWage: previewEmployee.overtimeActualWage,
          overtimeBasicWage: previewEmployee.overtimeBasicWage,
          regularOvertimeHours: previewEmployee.regularOvertimeHours,
          premiumOvertimeHours: previewEmployee.premiumOvertimeHours,
          pensionDeduction: previewEmployee.pensionDeduction,
          gosiAniEmployer: previewEmployee.gosiAniEmployer,
          gosiAniEmployee: previewEmployee.gosiAniEmployee,
          gosiOhEmployer: previewEmployee.gosiOhEmployer,
          gosiSanedEmployer: previewEmployee.gosiSanedEmployer,
          gosiSanedEmployee: previewEmployee.gosiSanedEmployee,
          cchiEmployeeShare: previewEmployee.cchiEmployeeShare,
          hourlyStatutoryBase: previewEmployee.hourlyStatutoryBase,
          includeVariablePay: previewEmployee.includeVariablePay,
          includeAdjustmentAdditions: previewEmployee.includeAdjustmentAdditions,
          includeAdjustmentDeductions: previewEmployee.includeAdjustmentDeductions,
          payslipStatus: previewEmployee.payslipStatus,
          payslipUrl: previewEmployee.payslipUrl,
          payrollDate: previewEmployee.payrollDate,
          createdOn: previewEmployee.createdOn,
        } satisfies PayslipData : null}
        payrollCycle={selectedCycle}
        currencySymbol={previewEmployee?.currencyCode || currencySymbol}
        onDownload={async (payslipId) => {
          await DownloadPayslipPdf(payslipId);
          showToast(t('Payslip downloaded successfully!'), 'success');
        }}
        onEmail={async (payslipId) => {
          const result = await BulkNotifyPayslips({ payslipIds: [payslipId] });
          if (result.status === 200 && !result.errors?.length) {
            showToast(t('Notification sent successfully'), 'success');
            setTimeout(() => loadEmployees(), 1000);
          } else {
            showToast(result.errors?.[0]?.description || t('Failed to send notification'), 'error');
          }
        }}
        onGenerate={() => previewEmployee && handlePayslipGenerate(previewEmployee)} />

      <Dialog open={bulkDialogOpen} onOpenChange={(open) => !isBulkProcessing && !open && setBulkDialogOpen(false)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('Confirm Bulk')} {bulkOperation ? bulkOperation.charAt(0).toUpperCase() + bulkOperation.slice(1) : ''}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground">{t('Are you sure you want to')} {bulkOperation} {selectedPayslips.length} {t('payslip(s)')}?</p>
            {bulkOperation === 'notify' && <Alert variant="info" className="mt-3"><AlertDescription>{t('Payslips will be sent to employees via email and push notifications.')}</AlertDescription></Alert>}
            {bulkOperation === 'generate' && <Alert variant="warning" className="mt-3"><AlertDescription>{t('This will generate PDFs for the selected payslips.')}</AlertDescription></Alert>}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={isBulkProcessing}>{t('Cancel')}</Button>
            <Button onClick={confirmBulkOperation} disabled={isBulkProcessing} loading={isBulkProcessing}>{isBulkProcessing ? t('Processing...') : t('Confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageLayout>
  );
};

// Connect to Redux to get company info and currency
const mapStateToProps = (state: any) => {
  const effectiveCurrency = state.user?.userRules?.currency;
  const currencySymbol = effectiveCurrency?.currencyCode || effectiveCurrency?.currencySymbol || 'AED';
  return {
    company: state.user?.company,
    currencySymbol,
  };
};

export default connect(mapStateToProps)(PayslipsPage);
